import { mergePopularidadEncabezado } from './popularidadReportModel';
import { formatPeriodoDescuentosLine } from './descuentosCortesiasReportModel';

export const VENTAS_POR_HORA_TITULO_DEFAULT = 'REPORTE VENTAS POR HORA';

function foldKeyLoose(k) {
  return String(k)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]/g, '')
    .toLowerCase();
}

function keyMatches(spec, rawKey) {
  return spec.patterns.some((re) => re.test(rawKey) || re.test(foldKeyLoose(rawKey)));
}

export const VENTAS_POR_HORA_COLUMN_SPECS = [
  {
    id: 'hora',
    label: 'Hora',
    patterns: [
      /^hora$/i,
      /^hour$/i,
      /^hr$/i,
      /^horadeldia$/i,
      /^horaDia$/i,
      /^horario$/i,
      /^intervalohora$/i,
    ],
    align: 'left',
    kind: 'hora',
  },
  {
    id: 'importe',
    label: 'Importe',
    patterns: [
      /^importe$/i,
      /^total$/i,
      /^venta$/i,
      /^monto$/i,
      /^importeventa$/i,
      /^importehora$/i,
      /^importeHora$/i,
    ],
    align: 'right',
    total: true,
    kind: 'money',
  },
];

/** @param {Record<string, unknown>|null|undefined} sampleRow */
export function resolveVentasPorHoraColumns(sampleRow) {
  const keys =
    sampleRow && typeof sampleRow === 'object' && !Array.isArray(sampleRow)
      ? Object.keys(sampleRow)
      : [];
  const used = new Set();
  return VENTAS_POR_HORA_COLUMN_SPECS.map((spec) => {
    const k = keys.find((kk) => !used.has(kk) && keyMatches(spec, kk));
    if (k) used.add(k);
    return { ...spec, sourceKey: k ?? null };
  });
}

/** @param {Record<string, unknown>} raw @param {ReturnType<typeof resolveVentasPorHoraColumns>} resolved */
export function rowToVentasPorHoraNorm(raw, resolved) {
  const o = {};
  for (const c of resolved) {
    o[c.id] = c.sourceKey ? raw[c.sourceKey] : undefined;
  }
  return o;
}

function fmtMoney2(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatVentasPorHoraCell(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : fmtMoney2(n);
  }
  if (spec.kind === 'hora') {
    const n = Number(value);
    if (!Number.isNaN(n)) return String(Math.trunc(n));
    return String(value);
  }
  return String(value);
}

export function formatVentasPorHoraTotalCell(spec, value) {
  if (!spec.total) return '—';
  if (value == null || Number.isNaN(Number(value))) return '—';
  return formatVentasPorHoraCell({ ...spec, kind: 'money' }, value);
}

/** @param {Record<string, unknown>[]} normRows @param {ReturnType<typeof resolveVentasPorHoraColumns>} resolved */
export function sumVentasPorHoraTotals(normRows, resolved) {
  const totals = {};
  for (const c of resolved) {
    if (!c.total) continue;
    let s = 0;
    for (const r of normRows) {
      const n = Number(r[c.id]);
      if (!Number.isNaN(n)) s += n;
    }
    totals[c.id] = s;
  }
  return totals;
}

const nFinite = (v) => {
  if (v == null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

export function mapApiVentasPorHoraTotalesToNormIds(apiTotals) {
  if (!apiTotals || typeof apiTotals !== 'object') return null;
  const out = {};
  for (const raw of [
    apiTotals.importe,
    apiTotals.total,
    apiTotals.totalImporte,
    apiTotals.total_importe,
  ]) {
    const x = nFinite(raw);
    if (x != null) {
      out.importe = x;
      break;
    }
  }
  return Object.keys(out).length ? out : null;
}

export function mergeVentasPorHoraFootTotals(summed, fromApi, resolved) {
  const base = { ...summed };
  if (!fromApi || typeof fromApi !== 'object') return base;
  for (const c of resolved) {
    if (!c.total) continue;
    if (fromApi[c.id] != null && !Number.isNaN(Number(fromApi[c.id]))) {
      base[c.id] = Number(fromApi[c.id]);
    }
  }
  return base;
}

/** @param {ReturnType<typeof resolveVentasPorHoraColumns>} resolved @param {Record<string, string>|null|undefined} columnas */
export function applyVentasPorHoraColumnLabelsFromApi(resolved, columnas) {
  if (!columnas || typeof columnas !== 'object' || Array.isArray(columnas)) return resolved;
  return resolved.map((c) => {
    if (!c.sourceKey) return c;
    const label = columnas[c.sourceKey];
    if (typeof label === 'string' && label.trim()) return { ...c, label: label.trim() };
    return c;
  });
}

export function mergeVentasPorHoraEncabezado(apiEnc, encabezadoFallback) {
  return mergePopularidadEncabezado(apiEnc, encabezadoFallback, {
    reporteTitulo: VENTAS_POR_HORA_TITULO_DEFAULT,
    incluirCortesias: false,
    lineaCortesias: '',
    cortesiasSubtitle: '',
  });
}

export function withVentasPorHoraPeriodoLine(enc, fechaInicio, fechaFin) {
  const e = enc && typeof enc === 'object' ? { ...enc } : {};
  if (!String(e.periodoTexto || '').trim() && (fechaInicio || fechaFin)) {
    e.periodoTexto = formatPeriodoDescuentosLine(fechaInicio, fechaFin);
  }
  return e;
}

/**
 * @param {Record<string, unknown>[]} rawRows
 * @param {ReturnType<typeof resolveVentasPorHoraColumns>} resolved
 */
export function sortVentasPorHoraRowsForDisplay(rawRows, resolved) {
  if (!rawRows.length) return rawRows;
  const horaSpec = resolved.find((c) => c.id === 'hora');
  const sk = horaSpec?.sourceKey;
  if (!sk) return [...rawRows];
  return [...rawRows].sort((a, b) => {
    const na = Number(a[sk]);
    const nb = Number(b[sk]);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a[sk] ?? '').localeCompare(String(b[sk] ?? ''), 'es', { numeric: true });
  });
}
